import { horizontalDividerSvg } from '../common'
import type { ImpactSummaryType, ImpactSolutionSummaryType, EmissionSourceType, GhgSavingPotentialType } from '~/types/types'
import { handleMetricAssignmentInfo } from './metric_assignment_info'
import { locale } from 'moment'
import { roundValue } from '~/functions/ghg'

export const handleAssumptions = (impactSummary: ImpactSummaryType, _locale: string) => {

  return [
    {
      text: 'Forecast, Assumptions and Limitations',
      width: '*',
      style: ['blueHeader'],
    },
    {
      text: 'The GHG emissions mitigated by each solution are calculated based on the estimated reduction potential per functional unit, as detailed in the Assumptions section.',
      style: 'message',
      margin: [0, 10, 0, 0],
    },
    {
      text: 'To forecast the GHG emissions mitigated, we use the target number of units we plan to sell or produce. When available, actual values are used to measure progress toward our targets.',
      style: 'message',
      margin: [0, 10, 0, 0],
    },
    ...(impactSummary?.impactSolutionSummaries?.map((solution) => assumptionsAndLimitations(solution, _locale)) ?? []),
  ]
}

const assumptionsAndLimitations = (solutionSummary: ImpactSolutionSummaryType, _locale: string) => {
  if (solutionSummary.forecastAssignment == undefined || solutionSummary.forecastValues == undefined || solutionSummary.totalGhgReduction == 'N/A') {
    return []
  }
  const metricInfo = handleMetricAssignmentInfo(
    solutionSummary.forecastAssignment,
    solutionSummary.forecastValues,
    _locale
  )

  const isScenarioA = solutionSummary.impactSolution.ghgSavingPotential?.isScenarioA

  return [{
    margin: [0, 10, 0, 0],
    columns: [
      {
        margin: [12, 0, 0, 0],
        columns: [
          {
            svg: `<svg width="21" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 11h3v2h-3zM1 11h3v2H1zM13 1v3h-2V1zM4.92 3.5l2.13 2.14l-1.42 1.41L3.5 4.93zm12.03 2.13l2.12-2.13l1.43 1.43l-2.13 2.12zM12 6a6 6 0 0 1 6 6c0 2.22-1.21 4.16-3 5.2V19a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-1.8c-1.79-1.04-3-2.98-3-5.2a6 6 0 0 1 6-6m2 15v1a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1zm-3-3h2v-2.13c1.73-.44 3-2.01 3-3.87a4 4 0 0 0-4-4a4 4 0 0 0-4 4c0 1.86 1.27 3.43 3 3.87z" fill="#5188ff"/>
            </svg>`,
            margin: [0,0,0,10],
            width: 20,
            height: 20,
          },
          {
            text: solutionSummary?.impactSolution.concept?.label,
            margin: [-20, 6, 0, 0],
            style: ['blueTextMedium'],
          },
        ],
      },
    ],
  },
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: "Forecast",
        style: ['blueTextSmall'],
      }
    ]
  },
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: solutionSummary.impactSolution.ghgSavingPotential?.isRecurringImpact ? "My solution leads to recurring savings for 4 years" : "My solution does not lead to recurring savings.",
        style: ['message'],
      }
    ]
  },
  {
    margin: [0, 10, 0, 0],
    columns: [
      ...(metricInfo ? [metricInfo] : [])
    ]
  },
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: "Assumptions",
        // margin: [12, 0, 0, 0],
        style: ['blueTextSmall'],
      }
    ]
  },
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: solutionSummary.impactSolution.ghgSavingPotential?.describeComparison ?? 'N/A',
        style: ['message'],
      }
    ]
  },
      ...(() => {
      if (isScenarioA) {
        const table = createGhgComparisonTable(solutionSummary.impactSolution.ghgSavingPotential?.emissionSources || [])
        return table ? [table] : []
      } else {
        const table = createScenarioAGhgComparisonTable(solutionSummary.impactSolution.ghgSavingPotential || null)
        return table ? [table] : []
      }
    })(),
  ...(!isScenarioA ? [{
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: solutionSummary.impactSolution.ghgSavingPotential?.scenarioBComments ?? 'N/A',
        style: ['message'],
      }
    ]
  }] : []),
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: "Limitations",
        // margin: [12, 0, 0, 0],
        style: ['blueTextSmall'],
      }
    ]
  },
  {
    margin: [12, 10, 0, 0],
    columns: [
      {
        text: solutionSummary.impactSolution.ghgSavingPotential?.emissionSourceLimitations ?? 'N/A',
        style: ['message'],
      }
    ]
  },
]
}

const createGhgComparisonTable = (emissionSources: EmissionSourceType[]) => {
  if (!emissionSources || emissionSources.length === 0) {
    return null
  }

  const tableBody = [
    [
      {
        text: 'I compare...',
        style: 'tableHeader',
        alignment: 'left',
      },
      {
        text: 'Baseline emissions',
        style: 'tableHeader',
        alignment: 'left',
      },
      {
        text: 'Solution emissions',
        style: 'tableHeader',
        alignment: 'left',
      },
    ],
    ...emissionSources.map((emissionSource) => [
      {
        text: emissionSource.sourceOfEmission,
        style: 'message',
        alignment: 'left',
      },
      {
        stack: [
          { text: emissionSource.baselineConceptName, style: 'messageBold' },
          { text: `${roundValue((emissionSource?.baselineGhgFactor ?? 0 * (emissionSource?.baselineConsumption ?? 0)))} kg CO2e per my functional unit`, style: 'messageBold' },
          { text: `Emission factor: ${roundValue(emissionSource.baselineGhgFactor || 0)} kg CO2e per ${emissionSource.baselineUnitName}`, style: 'messageSmall' },
          { text: `Amount: ${roundValue(emissionSource.baselineConsumption || 0)} ${emissionSource.baselineUnitName}`, style: 'messageSmall' },
          { text: `Source: ${emissionSource?.baselineEvidence}`, style: 'messageSmall' },
          { text: `Assumptions: ${emissionSource.baselineAssumption}`, style: 'messageSmall', italics: true },
        ],
        alignment: 'left',
      },
      {
        stack: [
          { text: emissionSource.solutionConceptName, style: 'messageBold' },
          { text: `${roundValue(emissionSource?.solutionGhgFactor ?? 0 * (emissionSource?.solutionConsumption ?? 0))} kg CO2e per my functional unit`, style: 'messageBold' },
          { text: `Emission factor: ${roundValue(emissionSource.solutionGhgFactor || 0)} kg CO2e per ${emissionSource.solutionUnitName}`, style: 'messageSmall' },
          { text: `Amount: ${roundValue(emissionSource.solutionConsumption || 0)} ${emissionSource.solutionUnitName}`, style: 'messageSmall' },
          { text: `Source: ${emissionSource?.solutionEvidence}`, style: 'messageSmall' },
          { text: `Assumptions: ${emissionSource.solutionAssumption}`, style: 'messageSmall', italics: true },
        ],
        alignment: 'left',
      },
    ]),
  ]

  return {
    layout: 'noBorders',
    margin: [12, 10, 0, 0],
    table: {
      headerRows: 1,
      widths: [120, 250, 250],
      body: tableBody,
    },
  }
}

const createScenarioAGhgComparisonTable = (ghgSavingsPotential: GhgSavingPotentialType | null) => {
  if (!ghgSavingsPotential) {
    return null
  }

  const tableBody = [
    [
      {
        text: 'I compare...',
        style: 'tableHeader',
        alignment: 'left',
      },
      {
        text: 'Baseline emissions',
        style: 'tableHeader',
        alignment: 'left',
      },
      {
        text: 'Solution emissions',
        style: 'tableHeader',
        alignment: 'left',
      },
    ],
    [
      {
        text: 'Overall GHG amount',
        style: 'message',
        alignment: 'left',
      },
      {
        stack: [
          { text: `${roundValue(ghgSavingsPotential?.preComputedBaselineGhg ?? 0)} kg per comparative unit`, style: 'message' },
        ],
        alignment: 'left',
      },
      {
        stack: [
          { text: `${roundValue(ghgSavingsPotential?.preComputedSolutionGhg ?? 0)} kg per comparative unit`, style: 'message' },
        ],
        alignment: 'left',
      },
    ],
  ]

  return {
    layout: 'noBorders',
    margin: [12, 10, 0, 0],
    table: {
      headerRows: 1,
      widths: [100, 200, 200],
      body: tableBody,
    },
  }
}
